// -----------------------------------------------------------
// FloraIQ - Fluxo Operacional (Página Principal)
// -----------------------------------------------------------
// Metodo de 7 etapas (definicao em config) + acoes de cadastramento reais
// (missoes de campo). Andamento por etapa, equipamentos, produtos gerados e
// pendencias nao tem fonte real ainda e aparecem como "sem dado real".

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { NoRealData } from "@/components/shared";
import { useCampaignsList } from "@/hooks";
import {
  operationalStages,
  operationalValidationRules,
  validationSeverityBadge,
  operationalDeliverables,
  operationalReportDefs,
} from "@/config/relat-oper-ladm-operacional-config";
import type { OperationalStageTab } from "@/config/relat-oper-ladm-operacional-config";
import CampaignsSection from "./components/CampaignsSection";
import StageMethodologyPanel from "./components/StageMethodologyPanel";
import PlannedProductsGrid from "./components/PlannedProductsGrid";

type TabId = "overview" | "actions" | OperationalStageTab | "deliverables" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "actions", label: "Ações de Cadastramento", icon: "ri-folder-chart-line" },
  ...operationalStages.map((s) => ({ id: s.tab as TabId, label: `Etapa ${s.id} — ${s.nome}`, icon: s.icon })),
  { id: "deliverables", label: "Produtos", icon: "ri-box-3-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

export default function OperationalFlowPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const { campaigns, loading, error } = useCampaignsList();

  const kpis = useMemo(() => {
    const emAndamento = campaigns.filter((c) => c.status === "EM_ANDAMENTO").length;
    const quadras = campaigns.reduce((acc, c) => acc + c.quadraCount, 0);
    const quadrasConcluidas = campaigns.reduce((acc, c) => acc + c.quadrasConcluidas, 0);
    const exemplares = campaigns.reduce((acc, c) => acc + c.registeredTreeCount, 0);
    return { emAndamento, quadras, quadrasConcluidas, exemplares };
  }, [campaigns]);

  const kpiValue = (v: string | number) => (loading || error ? "—" : v);

  const renderTab = () => {
    if (activeTab === "overview") {
      return (
        <div className="space-y-5">
          <div>
            <h4 className="text-sm font-bold text-foreground-900 mb-3">Etapas do método</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              {operationalStages.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveTab(s.tab)}
                  className="text-left p-3 rounded-lg border border-background-200/70 bg-background-50/50 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full bg-background-200 text-foreground-600 text-[10px] font-bold flex items-center justify-center">{s.id}</span>
                    <span className="text-xs font-semibold text-foreground-800">{s.nome}</span>
                  </div>
                  <p className="text-[11px] text-foreground-500">{s.objetivo}</p>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-sm font-bold text-foreground-900">Andamento das ações de cadastramento</h4>
              <CampaignsSection campaigns={campaigns} loading={loading} error={error} />
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-bold text-foreground-900">Etapas por situação e bloqueios</h4>
              <NoRealData
                icon="ri-bar-chart-horizontal-line"
                title="Sem registro de execução por etapa"
                description="Situação das etapas (concluída, em andamento, bloqueada) e bloqueios críticos aparecerão aqui quando o andamento por etapa passar a ser registrado."
              />
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "actions") {
      return <CampaignsSection campaigns={campaigns} loading={loading} error={error} />;
    }

    if (activeTab === "deliverables") {
      return (
        <PlannedProductsGrid
          title="Produtos previstos da ação"
          description="Entregáveis previstos ao fim da ação de cadastramento. Ainda não há geração nem histórico de produtos no sistema."
          items={operationalDeliverables.map((d) => ({ id: d.id, titulo: d.titulo, descricao: d.descricao, tags: [d.tipo, d.formato] }))}
        />
      );
    }

    if (activeTab === "reports") {
      return (
        <PlannedProductsGrid
          title="Relatórios operacionais previstos"
          description="Relatórios previstos para cada etapa. A geração de relatórios ainda não está disponível."
          items={operationalReportDefs.map((r) => ({ id: r.id, titulo: r.titulo, descricao: r.descricao, tags: [r.etapaRelacionada] }))}
        />
      );
    }

    const stage = operationalStages.find((s) => s.tab === activeTab);
    if (!stage) return null;

    if (stage.tab === "field") {
      return (
        <StageMethodologyPanel
          stage={stage}
          noDataDescription="Produção por técnico, cobertura por bairro e sincronização do coletor ainda não têm fonte consolidada. O andamento real das missões está abaixo."
        >
          <div className="space-y-2">
            <h5 className="text-xs font-semibold text-foreground-700">Missões de campo</h5>
            <CampaignsSection campaigns={campaigns} loading={loading} error={error} />
          </div>
        </StageMethodologyPanel>
      );
    }

    if (stage.tab === "processing3d") {
      return (
        <StageMethodologyPanel
          stage={stage}
          noDataDescription="O acompanhamento do processamento por levantamento ainda não tem fonte de dados."
        >
          <p className="text-xs text-foreground-500">
            As métricas 3D já vinculadas aos exemplares estão em{" "}
            <Link to="/nuvem3d" className="text-primary-600 hover:underline">Nuvem de Pontos</Link>.
          </p>
        </StageMethodologyPanel>
      );
    }

    if (stage.tab === "validation") {
      return (
        <StageMethodologyPanel
          stage={stage}
          noDataDescription="Os resultados por regra (avaliados, conformes, inconformes) ainda não são calculados por ação de cadastramento."
        >
          <div>
            <h5 className="text-xs font-semibold text-foreground-700 mb-2">Regras de validação</h5>
            <div className="space-y-1.5">
              {operationalValidationRules.map((r) => (
                <div key={r.regra} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-background-50/70 border border-background-100/70">
                  <span className="text-xs text-foreground-700">{r.regra}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${validationSeverityBadge[r.severidade]}`}>{r.severidade}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-foreground-500 mt-3">
              As inconsistências reais do cadastro estão em{" "}
              <Link to="/qualidade" className="text-primary-600 hover:underline">Qualidade de Dados</Link>.
            </p>
          </div>
        </StageMethodologyPanel>
      );
    }

    return <StageMethodologyPanel stage={stage} />;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fluxo Operacional"
        subtitle="Etapas da ação de cadastramento arbóreo, do planejamento à publicação dos produtos técnicos, conforme a metodologia FloraIQ-LADM."
      />

      {/* KPIs reais, derivados das missões de campo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard label="Ações de cadastramento" value={kpiValue(campaigns.length)} icon="ri-folder-chart-line" variant="info" />
        <KpiCard label="Em andamento" value={kpiValue(kpis.emAndamento)} icon="ri-play-circle-line" variant="info" />
        <KpiCard
          label="Quadras concluídas"
          value={kpiValue(kpis.quadras > 0 ? `${kpis.quadrasConcluidas}/${kpis.quadras}` : "—")}
          icon="ri-layout-grid-line"
          variant="success"
        />
        <KpiCard label="Exemplares registrados" value={kpiValue(kpis.exemplares.toLocaleString("pt-BR"))} icon="ri-plant-line" variant="success" />
      </div>

      <div className="bg-accent-50/60 border border-accent-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-accent-600"></i>
          </div>
          <p>
            O Fluxo Operacional organiza a ação de cadastramento arbóreo em 7 etapas sequenciais. As ações de cadastramento e seus números vêm das
            missões de campo; o andamento detalhado de cada etapa ainda não é registrado pelo sistema.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-accent-600 border-accent-500 bg-accent-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>
    </div>
  );
}

